import { useEffect, useRef } from 'react';
import {
  createMarkerContainer,
  createMarkerImg,
  createLinkBadge,
  createTooltip,
  showTooltip,
  hideTooltip
} from '../utils.ts';

type GeometryItem = {
  extent: [number, number, number, number];
  label?: string;
  points?: [number, number][];
  lines?: [number, number][][];
};

type ClientPos = { clientX: number; clientY: number };

type GeometryMarkerProps = {
  map: google.maps.Map | null;
  isLoaded: boolean;
  locationData?: GeometryItem[];
  markerIcon?: string;
  onSelectLocation?: (item: GeometryItem, clickedPoint?: { lat: number; lng: number }, pos?: ClientPos) => void;
  onRightClick?: (item: GeometryItem, pos: ClientPos) => void;
};

function latLngToClientPos(map: google.maps.Map, lat: number, lng: number): ClientPos | null {
  const projection = map.getProjection();
  const bounds = map.getBounds();
  if (!projection || !bounds) return null;
  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
  if (!topRight || !bottomLeft) return null;
  const scale = Math.pow(2, map.getZoom() ?? 0);
  const worldPoint = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
  if (!worldPoint) return null;
  const rect = map.getDiv().getBoundingClientRect();
  return {
    clientX: (worldPoint.x - bottomLeft.x) * scale + rect.left,
    clientY: (worldPoint.y - topRight.y) * scale + rect.top
  };
}

export function GeometryMarker({
  map,
  isLoaded,
  locationData,
  markerIcon,
  onSelectLocation,
  onRightClick
}: GeometryMarkerProps) {
  const onSelectLocationRef = useRef(onSelectLocation);
  useEffect(() => {
    onSelectLocationRef.current = onSelectLocation;
  }, [onSelectLocation]);

  const onRightClickRef = useRef(onRightClick);
  useEffect(() => {
    onRightClickRef.current = onRightClick;
  }, [onRightClick]);

  useEffect(() => {
    if (!isLoaded || !map) return;
    if (!google.maps.marker?.AdvancedMarkerElement) return;
    if (!locationData?.length) return;

    const mapDiv = map.getDiv();
    const tooltip = createTooltip();
    mapDiv.appendChild(tooltip);

    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    const listeners: google.maps.MapsEventListener[] = [];

    locationData.forEach((item: any) => {
      const label: string | undefined = item.label;
      var iotDeviceCount = item.iot_device_count;
      // 포인트 마커
      item.points?.forEach(([lng, lat]: any) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const container = createMarkerContainer(40);
        if (markerIcon) {
          container.appendChild(createMarkerImg(markerIcon, label || '', 40));
        }
        if (iotDeviceCount > 0) {
          const badge = createLinkBadge(label ?? ' ', '/assets/icon/map/link-img.svg');
          if (badge) container.appendChild(badge);
        }

        container.addEventListener('contextmenu', (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const pos = map ? latLngToClientPos(map, lat, lng) : null;
          onRightClickRef.current?.(item, pos ?? { clientX: e.clientX, clientY: e.clientY });
        });

        if (label) {
          container.addEventListener('mouseenter', (e: MouseEvent) => {
            const rect = mapDiv.getBoundingClientRect();
            showTooltip(
              tooltip,
              [{ label: 'PTW No', value: label }],
              e.clientX - rect.left,
              e.clientY - rect.top
            );
          });
          container.addEventListener('mousemove', (e: MouseEvent) => {
            const rect = mapDiv.getBoundingClientRect();
            showTooltip(
              tooltip,
              [{ label: 'PTW No', value: label }],
              e.clientX - rect.left,
              e.clientY - rect.top
            );
          });
          container.addEventListener('mouseleave', () => hideTooltip(tooltip));
        }

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat, lng },
          content: container,
          gmpClickable: true
        });

        const handler = () => {
          const pos = map ? latLngToClientPos(map, lat, lng) : undefined;
          onSelectLocationRef.current?.(item, { lat, lng }, pos ?? undefined);
        };

        marker.addEventListener('gmp-click', handler);

        markers.push(marker);
      });

    });

    return () => {
      markers.forEach((m) => (m.map = null));
      listeners.forEach((l) => l.remove());
      tooltip.remove();
    };
  }, [map, isLoaded, locationData]);

  return null;
}
